import { Ref, computed, inject, provide, ComputedRef, ref } from "vue";
import { Button, Brand, AdTag, Operator } from "@fresh8/types/src/index";

export function createBrand(brand: Ref<Brand>) {
  const adTemplate = computed(() => brand.value?.adTemplate);
  const buttons = computed(() => brand.value?.buttons);
  const fonts = computed(() => brand.value?.fonts);
  const colors = computed(() => brand.value?.colors);
  const marketBranding = computed(() => brand.value?.adTemplate.market);
  const footerBranding = computed(() => brand.value?.adTemplate.footer);
  const headerBranding = computed(() => brand.value?.adTemplate.header);
  const operatorLogos = computed(() => brand.value?.operatorLogos);
  const offers = computed(() => brand.value?.offers);

  const operatorHasOffer = (operatorId: string) => {
    const operatorOffer = offers.value?.[operatorId];

    return (
      operatorOffer &&
      ((operatorOffer.body.length || 0) > 0 ||
        (operatorOffer.header.length || 0) > 0 ||
        (operatorOffer.buttonText.length || 0) > 0 ||
        (operatorOffer.terms.length || 0) > 0)
    );
  };

  const getColor = (colorKey: string) => colors.value?.[colorKey]?.color;

  const adTemplateBranding = computed(() => {
    return {
      backgroundColor: getColor(adTemplate.value?.backgroundColor),
      borderRadius: adTemplate.value?.borderRadius,
      marketBorderColor:
        getColor(adTemplate.value?.marketBorderColor) || "#dcddf"
    };
  });

  const getColorStyle = (colorId: string) => colors.value?.[colorId]?.color;

  const getFontStyle = (fontId: string, fallback?: boolean) => {
    let font = fonts.value[fontId];

    if (fallback && !font) {
      font = fonts.value[Object.keys(fonts.value)[0]];
    }

    if (!font) return undefined;

    const { fontAsset, ...fontRest } = font;

    return {
      ...fontRest,
      color: getColorStyle(font.color),
      fontFamily: fontAsset.fontFamily
    };
  };

  function getButtonStateColors(action) {
    return {
      primaryColor: getColorStyle(action.primaryColor),
      secondaryColor: getColorStyle(action.secondaryColor),
      backgroundColor: getColorStyle(action.backgroundColor),
      borderColor: getColorStyle(action.borderColor)
    };
  }

  const getButtonStyle = (buttonId: string, fallback?: boolean) => {
    let button: Button = buttons.value[buttonId];
    const defaultButton = Object.keys(buttons.value)[0];

    if (fallback && !button && defaultButton) {
      button = buttons.value[defaultButton];
    } else if (fallback && !button && !defaultButton) {
      button = {
        name: "",
        type: "odds",
        primaryFont: "",
        secondaryFont: "",
        autoInteractions: true,
        iconColor: "",
        actions: {}
      };
    }

    return {
      ...button,
      iconColor: getColorStyle(button?.iconColor || "#000"),
      backgroundColor: getColorStyle(button?.backgroundColor || ""),
      borderColor: getColorStyle(button?.borderColor || ""),
      actions: {
        hover: button?.actions.hover
          ? getButtonStateColors(button.actions.hover)
          : undefined,
        active: button?.actions.active
          ? getButtonStateColors(button.actions.active)
          : undefined
      }
    };
  };

  return {
    adTemplate,
    brand,
    fonts,
    buttons,
    offers,
    adTemplateBranding,
    headerBranding,
    marketBranding,
    footerBranding,
    operatorLogos,
    getColorStyle,
    getFontStyle,
    getButtonStyle,
    operatorHasOffer
  };
}

export type BrandContext = ReturnType<typeof createBrand>;
export type FontStyle = ReturnType<BrandContext["getFontStyle"]>;
export type ButtonStyle = ReturnType<BrandContext["getButtonStyle"]>;

export const brandSymbol = Symbol("brandSymbol");

export function provideBrand(brand: Ref<Brand>) {
  provide(brandSymbol, createBrand(brand));
}

export function injectBrand() {
  return inject<BrandContext>(brandSymbol);
}

export function createAdTag(adTag: Ref<AdTag>) {
  const responsive = computed(() => adTag.value?.responsive);
  const adTagSize = computed(() => adTag.value?.size);
  const operators = computed(() => adTag.value?.operators);

  const selectedOperator = ref<Operator>();

  const randomOperator = computed(() => {
    if (operators.value?.length > 0) {
      const randowSelection = Math.floor(
        Math.random() * operators.value.length
      );
      return operators.value[randowSelection];
    }
    return adTag.value?.operator;
  });

  const operator = computed(() => {
    if (selectedOperator.value) {
      return selectedOperator.value;
    }

    if (adTag.value?.operator) {
      return adTag.value?.operator;
    }

    return randomOperator.value;
  });

  return {
    responsive,
    adTagSize,
    adTag,
    operator,
    operators,
    randomOperator,
    selectedOperator
  };
}

export type AdTagContext = ReturnType<typeof createAdTag>;
export const adTagSymbol = Symbol("AdTagSymbol");

export function provideAdTag(adTag: Ref<AdTag>) {
  provide(adTagSymbol, createAdTag(adTag));
}

export function injectAdTag() {
  return inject<AdTagContext>(adTagSymbol);
}

export const UseBrandOfferSymbol = Symbol("UseOfferSymbol");
export function provideUseBrandOffer(useBrandOffer: ComputedRef<boolean>) {
  provide(UseBrandOfferSymbol, useBrandOffer);
}
export function injectUseBrandOffer() {
  return inject<ComputedRef<boolean>>(UseBrandOfferSymbol);
}
